const STORAGE_KEY = 'preload_error_reloaded_at';
const RELOAD_WINDOW_MS = 30_000;

interface Options {
  // all three are swapped in tests: jsdom can neither navigate nor keep time
  reload?: () => void;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  now?: () => number;
}

/**
 * Reload the page once when a lazily loaded route chunk or stylesheet fails
 * to download, instead of leaving the visitor on a broken page.
 *
 * Vite dispatches `vite:preloadError` for that failure. Weak mobile signal
 * and in-app browsers trigger it on the checkout pages all day, and a fresh
 * load fixes it. A second failure within the window is left to the error
 * boundary, which offers a manual reload, so a dead connection never loops.
 * The timestamp lives in sessionStorage so the guard survives the reload;
 * where storage is unavailable (private mode, some WebViews) an in-memory
 * flag still protects the current page load.
 */
export function installReloadOnPreloadError({
  reload = () => window.location.reload(),
  storage = safeSessionStorage(),
  now = Date.now,
}: Options = {}): () => void {
  let reloading = false;

  const onPreloadError = (event: Event) => {
    if (reloading) {
      // a reload is already on its way; no need to flash the error screen
      event.preventDefault();
      return;
    }
    if (reloadedRecently(storage, now())) {
      return;
    }
    reloading = true;
    remember(storage, now());
    event.preventDefault();
    reload();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}

function reloadedRecently(storage: Options['storage'], at: number): boolean {
  try {
    const last = Number(storage?.getItem(STORAGE_KEY));
    return last > 0 && at - last < RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

function remember(storage: Options['storage'], at: number): void {
  try {
    storage?.setItem(STORAGE_KEY, String(at));
  } catch {
    // storage is read-only or full: the in-memory flag still covers this load
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
